/**
 * The Osteo3d team's OFFICIAL colour palette — one home, shared by every app that renders anatomy.
 *
 * SOURCE OF TRUTH: the team's own palette, given 2026-06-21, recorded verbatim in
 * `~/.claude/projects/-Users-deepakrajk-Documents-GitHub-Osteo3d/memory/osteo3d-color-codes.md`.
 * Do not edit these values from a screenshot, a render, or a description — go back to that file.
 *
 * The design app's case-report builder independently CONFIRMED these rather than trusting them, by
 * sampling pixels from the shipped demo PDFs and comparing hues:
 *
 *   part                official   measured p90 on the page   hue match
 *   bone / cranium      #CFC1A7    #DED2AE family             yes
 *   maxilla / Le Fort   #8893F7    #7577AB                    yes (~233°)
 *   mandible / BSSO     #91DD6A    #90C861                    yes (~95°)
 *   genioplasty         #D684E1    #C875AB                    yes
 *
 * ⚠️ The measured on-page values are DARKER than the albedo because the renders are shaded.
 * Do NOT "correct" these constants towards the measured numbers or every model comes out muddy.
 *
 * WHY THIS PACKAGE EXISTS: the planning engine had been rendering Tailwind approximations inferred
 * from the report spec's wording ("maxilla blue, mandible green") while the real hex values sat in
 * memory unused. Two apps drawing the same anatomy in different colours is a defect you only notice
 * by holding two PDFs side by side, so the values live in one place now.
 *
 * NOTE ON RESOLUTION: `packages/` currently contains only Python packages and is NOT in the root
 * npm `workspaces` list, so consumers import this by relative path rather than by package name.
 * Adding "packages/*" to the root workspaces would make `@osteo3d/palette` resolve normally — worth
 * doing when someone is already touching the root lockfile.
 */

/** Illustration / bone tones. Also the SSE PDF report's "Bone tone" presets. */
export const BONE_TONES = [
  ['Bone', '#CFC1A7'],
  ['Beige', '#ECD8C6'],
  ['Bone Z', '#F5EBCB'],
];

export const BONE = '#CFC1A7';

export const OFFICIAL_COLORS = {
  // illustration / bone
  bone: '#CFC1A7',
  beige: '#ECD8C6',
  boneZ: '#F5EBCB',
  lightBlue: '#52BEED',
  oliveGreen: '#A1B579',
  oliveGreenZbr: '#555B46',
  babyPink: '#F4C2C2',
  red: '#CC4343',
  bambuGreen: '#91DD6A',

  // planning images / case report — anatomy coding
  resection: '#FF8F8F',
  inferiorAlveolarNerve: '#97F6F6',
  bloodVessels: '#FF7276',
  skinLateralMalleolus: '#AE9076',
  mockImplant: '#B2EE91',

  // orthognathic
  osteotomy: '#8893F7',          // Le Fort I / BSSO cut segment — the report's "maxilla" colour
  maxillaMovement: '#88C9FC',
  mandibleOsteotomy: '#91DD6A',
  genioplasty: '#D684E1',
  trimming: '#F95757',

  // Fabrication outputs print in a neutral resin tone. NOT from the 2026-06-21 team memo — this is
  // the design app's convention, adopted here so both apps agree. Confirm with the team if it ever
  // matters clinically.
  splint: '#E8E8EA',
  guide: '#E8E8EA',
};

/** Left/right differ only in Seg01 <-> Seg03 (orange <-> blue). */
export const FIBULA_SEGMENTS = {
  left: ['#F6AB7E', '#BAF67E', '#7988E0', '#FC9AF7', '#F7FA48'],
  right: ['#7988E0', '#BAF67E', '#F6AB7E', '#FC9AF7', '#F7FA48'],
};

/**
 * Role -> colour for an orthognathic case. Roles are the shared vocabulary emitted by the planning
 * engine's mesh classifier (see apps/surgical-planning-engine/sidecar/main.py::_ROLE_RULES) and
 * consumed by the design app's case-report builder — keep the two in step.
 */
export const ORTHOGNATHIC_ROLES = {
  skull: BONE,
  cranium: BONE,
  maxilla: OFFICIAL_COLORS.osteotomy,
  maxilla_teeth: OFFICIAL_COLORS.osteotomy,
  mandible: OFFICIAL_COLORS.mandibleOsteotomy,
  mandible_teeth: OFFICIAL_COLORS.mandibleOsteotomy,
  genio: OFFICIAL_COLORS.genioplasty,          // planning-engine role name
  genio_segment: OFFICIAL_COLORS.genioplasty,  // design app's role name for the same thing
  mandibular_canal: OFFICIAL_COLORS.inferiorAlveolarNerve,
  upper_teeth: OFFICIAL_COLORS.splint,
  lower_teeth: OFFICIAL_COLORS.splint,
  teeth: OFFICIAL_COLORS.splint,
  splint: OFFICIAL_COLORS.splint,
  splint_intermediate: OFFICIAL_COLORS.splint,
  splint_final: OFFICIAL_COLORS.splint,
  splint_movement: OFFICIAL_COLORS.splint,
  guide: OFFICIAL_COLORS.guide,
  ios: OFFICIAL_COLORS.splint,
  ios_upper: OFFICIAL_COLORS.splint,
  ios_lower: OFFICIAL_COLORS.splint,
  ios_buccal: OFFICIAL_COLORS.splint,
  ios_bite: OFFICIAL_COLORS.splint,
  other: BONE,
};

/**
 * Role -> colour for a RECONSTRUCTION case. Every value here already existed in OFFICIAL_COLORS or
 * FIBULA_SEGMENTS above; nothing new is invented, and no orthognathic value is changed.
 *
 * The donor bones are plain bone tone: un-cut anatomy, and there is no team colour that means
 * "fibula". The placed SEGMENTS are what carry colour, from FIBULA_SEGMENTS, indexed by segment
 * number and differing left/right — which is the team's own convention and the reason that table
 * exists. The resected specimen takes the report's `resection` colour, because that is exactly what
 * it is in a delivered case report.
 */
export const RECONSTRUCTION_ROLES = {
  donor_leg: BONE,
  fibula: BONE,
  tibia: BONE,
  resected_segment: OFFICIAL_COLORS.resection,
  remaining_bone: BONE,
  neomandible: BONE,
  guide: OFFICIAL_COLORS.guide,
  other: BONE,
};

/** Colour for a segment of the graft. `index` is 0-based; `side` is the DONOR side, not the defect. */
export const fibulaSegmentColor = (index, side = "left") => {
  const set = FIBULA_SEGMENTS[side] || FIBULA_SEGMENTS.left;
  return set[index % set.length];
};

export const roleColor = (role) => ORTHOGNATHIC_ROLES[role] || BONE;

/** Colour for a role in a named track. Falls back to bone rather than inventing a hue. */
export const roleColorForTrack = (role, track) => (
  track === "reconstruction"
    ? (RECONSTRUCTION_ROLES[role] || ORTHOGNATHIC_ROLES[role] || BONE)
    : (ORTHOGNATHIC_ROLES[role] || BONE)
);


/**
 * The planning engine's de-identification key file — the id → patient-named-filename map written
 * BESIDE report.json and never allowed to travel with it (report.json is de-identified BECAUSE
 * this file stays behind). One name, here in the shared vocabulary package, because the 2026-08-30
 * review found it duplicated across both apps in two different vocabularies: a rename on the
 * engine side would have silently defeated the design builder's folder warning.
 *
 * The two PYTHON sites that cannot import this (sidecar report.py writes the file; sidecar
 * main.py's save-outputs refuses it) carry cross-reference comments — update all together.
 */
export const LOCAL_KEY_FILENAME = "report.local.json";
